import { useMemo, useRef } from 'react';
import { Button, Textarea, Tooltip } from '@heroui/react';
import { ArrowUp, Link2, Image as ImageIcon, List, X, Plus, Paperclip } from 'lucide-react';
import { nanoid } from 'nanoid';
import { useToast } from '@/shared/lib/hooks';
import type { AttachmentMeta } from '../../model/composerDraftsSlice';
import { useComposerDraft } from '../../hooks/useComposerDraft';
import { composerFileStore } from '../../model/composerFileStore';

const MAX_ATTACHMENTS = 3;
const MAX_FILE_SIZE = 20 * 1024 * 1024;

const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);
const ALLOWED_DOC_EXTENSIONS = new Set([
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'csv',
]);
const ALLOWED_DOC_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
]);

type SubmitPayload = {
  text: string;
  files: File[];
  attachmentsMeta: AttachmentMeta[];
};

type MessageComposerProps = {
  draftKey: string;
  withLayout?: boolean;
  onSubmit?: (payload: SubmitPayload) => Promise<void> | void;

  placeholder?: string;
  disabled?: boolean;
  isSubmitting?: boolean;
  initialText?: string; // Начальный текст
  maxAttachments?: number; // Максимальное количество файлов
};

function replaceSelection(
  el: HTMLTextAreaElement,
  next: string,
  selectInserted = false,
): { value: string; nextCursorStart: number; nextCursorEnd: number } {
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  const value = el.value;
  const before = value.slice(0, start);
  const after = value.slice(end);
  const newValue = before + next + after;

  const cursorStart = selectInserted ? start : start + next.length;
  const cursorEnd = selectInserted ? start + next.length : start + next.length;
  return { value: newValue, nextCursorStart: cursorStart, nextCursorEnd: cursorEnd };
}

function prefixLinesWithDash(text: string) {
  const lines = text.split('\n');
  const next = lines.map((l) => (l.trim().length === 0 ? l : l.startsWith('- ') ? l : `- ${l}`));
  return next.join('\n');
}

const ComposerLayout = ({
  children,
  withLayout = false,
}: {
  children: React.ReactNode;
  withLayout?: boolean;
}) => (
  <>
    {withLayout ? <div className="w-full rounded-[24px] bg-white p-6">{children}</div> : children}
  </>
);

export const MessageComposer = (props: MessageComposerProps) => {
  const {
    draftKey,
    onSubmit,
    placeholder = 'Your text....',
    disabled = false,
    isSubmitting = false,
    initialText,
    maxAttachments = MAX_ATTACHMENTS,
    withLayout,
  } = props;

  const { toast } = useToast();

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { text, attachments, setText, addAttachments, removeAttachment, clear } = useComposerDraft(
    draftKey,
    initialText,
  );

  const canSubmit = useMemo(() => {
    if (disabled || isSubmitting) return false;
    return text.trim().length > 0 || attachments.length > 0;
  }, [disabled, isSubmitting, text, attachments.length]);

  const imagesInputRef = useRef<HTMLInputElement | null>(null);
  const filesInputRef = useRef<HTMLInputElement | null>(null);

  const handleInsertLink = () => {
    const el = textareaRef.current;
    if (!el) return;

    const selStart = el.selectionStart ?? 0;
    const selEnd = el.selectionEnd ?? 0;
    const selected = el.value.slice(selStart, selEnd);

    const url = window.prompt('Link URL:', 'https://');
    if (!url) return;

    const linkText = selected?.trim()
      ? selected
      : window.prompt('Link text:', 'link text') || 'link text';
    const markdown = `[${linkText}](${url})`;

    const { value, nextCursorStart, nextCursorEnd } = replaceSelection(el, markdown);
    setText(value);

    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(nextCursorStart, nextCursorEnd);
    });
  };

  const handleList = () => {
    const el = textareaRef.current;
    if (!el) return;

    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;

    if (start === end) {
      const { value, nextCursorStart, nextCursorEnd } = replaceSelection(el, '- ');
      setText(value);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(nextCursorStart, nextCursorEnd);
      });
      return;
    }

    const selected = el.value.slice(start, end);
    const nextSelected = prefixLinesWithDash(selected);

    const { value, nextCursorStart, nextCursorEnd } = replaceSelection(el, nextSelected, true);
    setText(value);

    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(nextCursorStart, nextCursorEnd);
    });
  };

  const acceptFiles = (fileList: FileList | null, kind: 'image' | 'file') => {
    if (!fileList || fileList.length === 0) return;

    const availableSlots = maxAttachments - attachments.length;
    if (availableSlots <= 0) {
      toast({
        title: 'Attachment limit reached',
        description: `You can upload up to ${maxAttachments > 1 ? `${maxAttachments} files` : '1 file'}.`,
        color: 'warning',
      });
      return;
    }

    const getFileExtension = (name: string) => {
      const index = name.lastIndexOf('.');
      return index >= 0 ? name.slice(index + 1).toLowerCase() : '';
    };

    const isAllowedDocument = (file: File) =>
      ALLOWED_DOC_MIME_TYPES.has(file.type) ||
      ALLOWED_DOC_EXTENSIONS.has(getFileExtension(file.name));

    const metas: AttachmentMeta[] = [];
    let rejectedTooLarge = 0;
    let rejectedType = 0;
    let rejectedLimit = 0;

    Array.from(fileList).forEach((file) => {
      if (metas.length >= availableSlots) {
        rejectedLimit += 1;
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        rejectedTooLarge += 1;
        return;
      }

      if (kind === 'image') {
        if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
          rejectedType += 1;
          return;
        }
      } else if (!isAllowedDocument(file)) {
        rejectedType += 1;
        return;
      }

      const id = nanoid();
      metas.push({
        id,
        name: file.name,
        size: file.size,
        mime: file.type || 'application/octet-stream',
        kind,
      });
      composerFileStore.set(id, file);
    });

    if (rejectedLimit > 0) {
      toast({
        title: 'Attachment limit',
        description: `Only ${maxAttachments} files allowed.`,
        color: 'warning',
      });
    }

    if (rejectedTooLarge > 0) {
      toast({
        title: 'File too large',
        description: 'Each file must be 20MB or less.',
        color: 'warning',
      });
    }

    if (rejectedType > 0) {
      toast({
        title: 'Unsupported file type',
        description:
          'Only images (JPEG, PNG, GIF, WEBP) and documents (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV) are allowed.',
        color: 'warning',
      });
    }

    addAttachments(metas);
  };

  const handleAddImages = () => imagesInputRef.current?.click();
  const handleAddMaterials = () => filesInputRef.current?.click();

  const handleRemoveAttachment = (id: string) => {
    composerFileStore.remove(id);
    removeAttachment(id);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const ids = attachments.map((a: any) => a.id);
    const files = composerFileStore.getMany(ids);

    try {
      await onSubmit?.({ text: text.trim(), files, attachmentsMeta: attachments });
      composerFileStore.clearMany(ids);
      clear();
    } catch (e) {
      console.log('Error submitting message', e);
    }
  };

  const handleClear = () => {
    const ids = attachments.map((a) => a.id);
    composerFileStore.clearMany(ids);
    clear();
  };

  return (
    <ComposerLayout withLayout={withLayout}>
      <div className="w-full rounded-[24px] border border-grey-border bg-white p-6">
        <input
          ref={imagesInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          className="hidden"
          onChange={(e) => {
            acceptFiles(e.target.files, 'image');
            // eslint-disable-next-line no-param-reassign
            e.currentTarget.value = '';
          }}
        />
        <input
          ref={filesInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
          multiple
          className="hidden"
          onChange={(e) => {
            acceptFiles(e.target.files, 'file');
            // eslint-disable-next-line no-param-reassign
            e.currentTarget.value = '';
          }}
        />

        <div className="min-h-[192px]">
          <Textarea
            ref={textareaRef as any}
            value={text}
            onValueChange={setText}
            placeholder={placeholder}
            isDisabled={disabled}
            minRows={9}
            className="w-full"
            classNames={{
              base: 'w-full',
              inputWrapper:
                'bg-transparent shadow-none border-0 p-0 data-[hover=true]:bg-transparent data-[focus=true]:ring-0 data-[focus=true]:ring-offset-0',
              input: 'resize-none text-base leading-6 placeholder:text-slate-400',
            }}
          />
        </div>

        {attachments.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {attachments.map((attachment: any) => (
              <div
                key={attachment.id}
                className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
              >
                <Paperclip className="h-4 w-4 text-slate-600" />
                <span className="max-w-[240px] truncate">{attachment.name}</span>
                <Button
                  isIconOnly
                  variant="light"
                  onPress={() => handleRemoveAttachment(attachment.id)}
                  className="h-5 w-5 min-w-5 ml-1 rounded-full p-1 text-slate-500 hover:bg-slate-200"
                  aria-label="Remove attachment"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <div>
            <Tooltip content="Clear" placement="top">
              <Button
                isIconOnly
                variant="flat"
                isDisabled={disabled}
                onPress={handleClear}
                className="h-11 w-11 rounded-full bg-red-50 text-red-500 hover:bg-red-100"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </Button>
            </Tooltip>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Tooltip content="Insert link" placement="top">
                <Button
                  isIconOnly
                  variant="flat"
                  isDisabled={disabled}
                  className="h-11 w-11 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200"
                  aria-label="Insert link"
                  onPress={handleInsertLink}
                >
                  <Link2 className="h-5 w-5" />
                </Button>
              </Tooltip>

              <Tooltip content="Add images" placement="top">
                <Button
                  isIconOnly
                  variant="flat"
                  isDisabled={disabled}
                  className="h-11 w-11 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200"
                  aria-label="Add images"
                  onPress={handleAddImages}
                >
                  <ImageIcon className="h-5 w-5" />
                </Button>
              </Tooltip>

              <Tooltip content="List" placement="top">
                <Button
                  isIconOnly
                  variant="flat"
                  isDisabled={disabled}
                  className="h-11 w-11 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200"
                  aria-label="List"
                  onPress={handleList}
                >
                  <List className="h-5 w-5" />
                </Button>
              </Tooltip>
            </div>

            <Button
              variant="flat"
              isDisabled={disabled}
              onPress={handleAddMaterials}
              startContent={<Plus className="h-5 w-5" />}
              className="h-11 rounded-full bg-sky-50 px-4 text-slate-800 hover:bg-sky-100"
            >
              Add materials
            </Button>

            <Tooltip content={canSubmit ? 'Submit' : 'Type text or add files'} placement="top">
              <span>
                <Button
                  isIconOnly
                  color="success"
                  variant="solid"
                  isDisabled={!canSubmit}
                  isLoading={isSubmitting}
                  onPress={handleSubmit}
                  className="h-11 w-11 rounded-full"
                  aria-label="Submit"
                >
                  <ArrowUp className="h-5 w-5" />
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>
    </ComposerLayout>
  );
};
